import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { CampaignsTableMeta } from '../../../../../Campaigns/types';
import { CellContext } from '@tanstack/react-table';
import { CampaignTableRow } from '../../types';
import { usePublicReportInstagramPosts } from 'Modules/Plans/PublicReports/Posts/InstagramPosts/hooks/usePublicReportInstagramPosts';
import { Dropdown, assets } from '@round/whitelabel-ui';
import styles from './ActionsCell.module.css';
import { useState } from 'react';
import { showNotification } from 'helpers';
import InstagramReportClipboardTable, {
    mapToClipboardReportRow,
    mapToClipboardReportTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/InstagramReportClipboardTable';
import copyComponentRenderToClipboard from 'utility/copyToClipboard/copyComponentRenderToClipboard';

const { Copy: CopyIcon } = assets;

const getTableMeta = getTableMetaHelper<CampaignsTableMeta>();

type Props = CellContext<CampaignTableRow, any>;

const ActionsCell = ({ table, row: { original } }: Props) => {
    const { isLoading, getIsCampaignStatsLoading } = getTableMeta(table);
    const { fetchImmediate } = usePublicReportInstagramPosts(original.id, { enabled: false });
    const [isCopyLoading, setIsCopyLoading] = useState(false);

    if (isLoading || getIsCampaignStatsLoading?.(original)) {
        return <Skeleton />;
    }

    const onCopyReport = async (campaign: CampaignTableRow) => {
        try {
            setIsCopyLoading(true);
            const result = await fetchImmediate();
            if (!result) {
                showNotification('Could not copy', 'error');
                return;
            }

            const { posts, users, userStats } = result;
            const items = posts.map((post) => {
                const accountId = post.instagram_details?.account_id;
                const user = users.find((u) => u.id === accountId) ?? null;
                return mapToClipboardReportRow({
                    post,
                    user,
                    userStats: userStats.find((u) => u.user_id === accountId) ?? null,
                });
            });

            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals: mapToClipboardReportTotals(campaign.stats, items),
                    items,
                    includeCost: posts.some((p) => 'cost' in p),
                },
                InstagramReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        } finally {
            setIsCopyLoading(false);
        }
    };

    return (
        <Dropdown>
            {({ setIsOpen }) => (
                <>
                    <Dropdown.Target />
                    <Dropdown.Menu>
                        <span className={styles.copyMenuTitle}>Copy</span>
                        <Dropdown.Item
                            icon={() => <CopyIcon className={styles.copyIcon} />}
                            onClick={() => {
                                onCopyReport(original).finally(() => {
                                    setIsOpen(false);
                                });
                            }}
                        >
                            {isCopyLoading ? 'Report loading...' : 'Report'}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </>
            )}
        </Dropdown>
    );
};

export default ActionsCell;
